import { NotFoundError, UnauthenticatedError } from '@domain/errors';
import type {
  Appointment,
  AppointmentDraft,
  Article,
  ArticleDraft,
  AuthenticatedAdmin,
  Company,
  CompanyDraft,
  Offer,
  OfferDraft,
} from '@domain/entities';
import type {
  AppointmentQuery,
  AppointmentRepository,
  ArticleQuery,
  ArticleRepository,
  AuthRepository,
  CompanyCreateInput,
  CompanyQuery,
  CompanyRepository,
  OfferCreateInput,
  OfferQuery,
  OfferRepository,
} from '@domain/repositories';
import { buildPage } from '@domain/value-objects';
import type {
  ModerationStatus,
  OfferStatus,
  Page,
  PublicationStatus,
} from '@domain/value-objects';
import { InMemoryDb, TODAY_LABEL, latency, matches } from './in-memory-db';

const SESSION_KEY = 'babimams.session';

/** Authentification simulée : le rôle dépend du préfixe de l'email. */
export class InMemoryAuthRepository implements AuthRepository {
  async signIn(email: string): Promise<AuthenticatedAdmin> {
    const role = email.startsWith('mod') ? 'ADMIN' : 'SUPER_ADMIN';
    const name = role === 'ADMIN' ? 'Marc Dubois' : 'Aïcha Koné';
    const admin: AuthenticatedAdmin = {
      id: role === 'ADMIN' ? 'admin1' : 'admin0',
      name,
      email,
      role,
      initials: name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase(),
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(admin));
    return latency(admin, 320);
  }

  async signOut(): Promise<void> {
    sessionStorage.removeItem(SESSION_KEY);
  }

  async restoreSession(): Promise<AuthenticatedAdmin | null> {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthenticatedAdmin;
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      throw new UnauthenticatedError('Session illisible, reconnectez-vous.');
    }
  }
}

export class InMemoryCompanyRepository implements CompanyRepository {
  constructor(private readonly db: InMemoryDb) {}

  async create(input: CompanyCreateInput): Promise<Company> {
    const user = this.db.users.find((u) => u.id === input.ownerId);
    const created: Company = {
      id: this.db.nextId('c'),
      name: input.name,
      type: input.type,
      city: input.city,
      email: user?.email ?? '',
      phone: null,
      ownerPhone: user?.phone ?? '',
      contactChannel: input.contactChannel,
      contactDestination: input.contactDestination,
      status: 'pending',
      submittedAt: new Date().toISOString(),
      approvedAt: null,
      rejectedAt: null,
      rejectionReason: null,
    };
    this.db.companies = [created, ...this.db.companies];
    return latency(created, 200);
  }

  async search(query: CompanyQuery): Promise<Page<Company>> {
    const filtered = this.db.companies.filter(
      (c) =>
        (query.status === 'all' || c.status === query.status) && matches(c.name, query.search),
    );
    return latency(buildPage(filtered, query));
  }

  async findById(id: string): Promise<Company> {
    const found = this.db.companies.find((c) => c.id === id);
    if (!found) throw new NotFoundError('Entreprise', id);
    return latency(found, 60);
  }

  async countByStatus(status: ModerationStatus): Promise<number> {
    return this.db.companies.filter((c) => c.status === status).length;
  }

  async listByStatus(status: ModerationStatus): Promise<Company[]> {
    return this.db.companies.filter((c) => c.status === status);
  }

  async update(id: string, draft: CompanyDraft): Promise<Company> {
    const index = this.db.companies.findIndex((c) => c.id === id);
    if (index === -1) throw new NotFoundError('Entreprise', id);
    const updated: Company = { ...this.db.companies[index], ...draft };
    this.db.companies[index] = updated;
    return latency(updated, 120);
  }

  async setStatus(ids: readonly string[], status: ModerationStatus): Promise<Company[]> {
    const idSet = new Set(ids);
    const updated: Company[] = [];
    this.db.companies = this.db.companies.map((c) => {
      if (!idSet.has(c.id)) return c;
      const next: Company = { ...c, status };
      updated.push(next);
      return next;
    });
    if (updated.length !== idSet.size) {
      throw new NotFoundError('Entreprise', ids.join(','));
    }
    return latency(updated, 120);
  }

  async remove(id: string): Promise<void> {
    const index = this.db.companies.findIndex((c) => c.id === id);
    if (index === -1) throw new NotFoundError('Entreprise', id);
    this.db.companies.splice(index, 1);
    return latency(undefined as void, 120);
  }
}

export class InMemoryOfferRepository implements OfferRepository {
  constructor(private readonly db: InMemoryDb) {}

  async create(input: OfferCreateInput): Promise<Offer> {
    const company = this.db.companies.find((c) => c.id === input.businessId);
    const formula = this.db.formulas.find((f) => f.slug === input.pricing);
    const created: Offer = {
      id: this.db.nextId('o'),
      title: input.title,
      company: company?.name ?? input.businessId,
      ownerType: 'business',
      category: input.category,
      pricing: input.pricing,
      priceAmount: formula?.amountXof ?? 0,
      description: input.description,
      clicks: 0,
      viewsCount: 0,
      callClicksCount: 0,
      whatsappClicksCount: 0,
      emailClicksCount: 0,
      status: input.publishNow ? 'active' : 'pending',
      publishedAt: input.publishNow ? new Date().toISOString() : null,
      expiresAt: input.expiresAt ?? (input.publishNow ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : null),
      createdAt: new Date().toISOString(),
      photoUrls: [],
      photoKeys: input.photoKeys,
      isPaid: false,
    };
    this.db.offers = [created, ...this.db.offers];
    return latency(created, 200);
  }

  async search(query: OfferQuery): Promise<Page<Offer>> {
    const filtered = this.db.offers.filter(
      (o) =>
        (query.status === 'all' || o.status === query.status) && matches(o.title, query.search),
    );
    return latency(buildPage(filtered, query));
  }

  async findById(id: string): Promise<Offer> {
    const found = this.db.offers.find((o) => o.id === id);
    if (!found) throw new NotFoundError('Offre', id);
    return latency(found, 60);
  }

  async countByStatus(status: OfferStatus): Promise<number> {
    return this.db.offers.filter((o) => o.status === status).length;
  }

  async listByStatus(status: OfferStatus): Promise<Offer[]> {
    return this.db.offers.filter((o) => o.status === status);
  }

  async topByClicks(limit: number): Promise<Offer[]> {
    return [...this.db.offers].sort((a, b) => b.clicks - a.clicks).slice(0, limit);
  }

  async update(id: string, draft: OfferDraft): Promise<Offer> {
    const index = this.db.offers.findIndex((o) => o.id === id);
    if (index === -1) throw new NotFoundError('Offre', id);
    const updated: Offer = { ...this.db.offers[index], ...draft };
    this.db.offers[index] = updated;
    return latency(updated, 120);
  }

  async setStatus(ids: readonly string[], status: OfferStatus): Promise<Offer[]> {
    const idSet = new Set(ids);
    const updated: Offer[] = [];
    this.db.offers = this.db.offers.map((o) => {
      if (!idSet.has(o.id)) return o;
      const next: Offer = { ...o, status };
      updated.push(next);
      return next;
    });
    if (updated.length !== idSet.size) throw new NotFoundError('Offre', ids.join(','));
    return latency(updated, 120);
  }

  async remove(id: string): Promise<void> {
    const idx = this.db.offers.findIndex((o) => o.id === id);
    if (idx === -1) throw new NotFoundError('Offre', id);
    this.db.offers.splice(idx, 1);
    await latency(undefined, 120);
  }
}

export class InMemoryArticleRepository implements ArticleRepository {
  constructor(private readonly db: InMemoryDb) {}

  async search(query: ArticleQuery): Promise<Page<Article>> {
    const filtered = this.db.articles.filter((a) => {
      const pubStatus: PublicationStatus = a.publishedAt !== null ? 'published' : 'draft';
      return (query.status === 'all' || pubStatus === query.status) && matches(a.title, query.search);
    });
    return latency(buildPage(filtered, query));
  }

  async findById(id: string): Promise<Article> {
    const found = this.db.articles.find((a) => a.id === id);
    if (!found) throw new NotFoundError('Article', id);
    return latency(found, 60);
  }

  async countByStatus(status: PublicationStatus): Promise<number> {
    return this.db.articles.filter((a) =>
      status === 'published' ? a.publishedAt !== null : a.publishedAt === null,
    ).length;
  }

  async create(draft: ArticleDraft): Promise<Article> {
    const created: Article = {
      id: this.db.nextId('a'),
      title: draft.title,
      body: draft.body,
      excerpt: draft.excerpt ?? null,
      categoryId: draft.categoryId ?? null,
      categoryLabel: null,
      coverUrl: null,
      publishedAt: draft.publishNow ? TODAY_LABEL : null,
      isFeatured: false,
      imageUrls: [],
      imageKeys: draft.imageKeys ?? [],
    };
    this.db.articles = [created, ...this.db.articles];
    return latency(created, 140);
  }

  async update(id: string, draft: ArticleDraft): Promise<Article> {
    const index = this.db.articles.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Article', id);
    const updated: Article = { ...this.db.articles[index], ...draft };
    this.db.articles[index] = updated;
    return latency(updated, 120);
  }

  async setStatus(ids: readonly string[], status: PublicationStatus): Promise<Article[]> {
    const idSet = new Set(ids);
    const updated: Article[] = [];
    this.db.articles = this.db.articles.map((a) => {
      if (!idSet.has(a.id)) return a;
      const next: Article = {
        ...a,
        publishedAt: status === 'published' ? (a.publishedAt ?? TODAY_LABEL) : null,
      };
      updated.push(next);
      return next;
    });
    if (updated.length !== idSet.size) throw new NotFoundError('Article', ids.join(','));
    return latency(updated, 120);
  }

  async remove(id: string): Promise<void> {
    const exists = this.db.articles.some((a) => a.id === id);
    if (!exists) throw new NotFoundError('Article', id);
    this.db.articles = this.db.articles.filter((a) => a.id !== id);
    await latency(null, 120);
  }
}

export class InMemoryAppointmentRepository implements AppointmentRepository {
  constructor(private readonly db: InMemoryDb) {}

  async search(query: AppointmentQuery): Promise<Page<Appointment>> {
    const now = new Date().toISOString();
    const filtered = this.db.appointments.filter((a) => {
      if (query.period === 'upcoming' && a.scheduledAt < now) return false;
      if (query.period === 'past' && a.scheduledAt >= now) return false;
      return matches(a.userName, query.search) ||
        matches(a.userEmail, query.search) ||
        matches(a.reason, query.search) ||
        matches(a.location, query.search);
    });
    return latency(buildPage(filtered, query));
  }

  async create(draft: AppointmentDraft): Promise<Appointment> {
    const user = this.db.users.find((u) => u.id === draft.userId);
    const created: Appointment = {
      id: this.db.nextId('appt'),
      userId: draft.userId,
      userName: user?.name ?? draft.userId,
      userEmail: user?.email ?? '',
      reason: draft.reason,
      scheduledAt: draft.scheduledAt,
      location: draft.location,
      reminderOffset: draft.reminderOffset,
      createdAt: new Date().toISOString(),
    };
    this.db.appointments = [created, ...this.db.appointments];
    return latency(created, 180);
  }

  async update(id: string, draft: Omit<AppointmentDraft, 'userId'>): Promise<Appointment> {
    const index = this.db.appointments.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Rendez-vous', id);
    const updated: Appointment = { ...this.db.appointments[index], ...draft };
    this.db.appointments[index] = updated;
    return latency(updated, 120);
  }

  async remove(id: string): Promise<void> {
    const exists = this.db.appointments.some((a) => a.id === id);
    if (!exists) throw new NotFoundError('Rendez-vous', id);
    this.db.appointments = this.db.appointments.filter((a) => a.id !== id);
    await latency(null, 120);
  }
}
